import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, finalize, map } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/services/notification.service';
import { CerereTypeService } from '../cerere-type.service';
import { CerereTypeAddPayload } from '../cerere-type.interface';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';

import { UserOrganigramaSearchPayload } from 'src/app/interfaces/common.interface';
import { Generic } from 'src/app/interfaces/generic.interface';
import { CommonService } from 'src/app/services/common.service';
import { CererePriorityService } from 'src/app/platform/nomenclatoare/cerere-priority/cerere-priority.service';
import { StatusService } from 'src/app/platform/nomenclatoare/cerere-status/status.service';

@Component({
    selector: 'app-add-cerere-type',
    templateUrl: './add-cerere-type.component.html',
    styleUrls: ['./add-cerere-type.component.scss'],
})
export class AddCerereTypeComponent implements OnInit {
    // general error
    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;
    helper = helper;
    token!: string;
    isLoading = false;

    form: FormGroup = new FormGroup({
        name: new FormControl('', [Validators.required]),
        slug: new FormControl('', [Validators.required]),
        priority_id: new FormControl('', [Validators.required]),
        status_id: new FormControl('', [Validators.required, Validators.min(0)]),
        directie_id: new FormControl('', [Validators.required, Validators.min(0)]),
        indoc_name: new FormControl('', []),
        indoc_id: new FormControl('', []),
        compartiment_id: new FormControl(null),
        user_id: new FormControl(null),
        observations: new FormControl(''),
    });

    parameters: { key: string; value: string }[] = [
        { key: '--Alegeți--', value: '-1' },
        { key: 'Da', value: '1' },
        { key: 'Nu', value: '0' },
    ];

    defaultParameter: { name: string; id: string } = {
        name: '--Alegeți--',
        id: '-1',
    };

    // directiiActive$: any = [];
    directiiActive$!: Observable<Generic[]>;
    compartimenteActive$!: Observable<Generic[]>;
    usersActive$!: Observable<Generic[]>;
    statusesActive$!: Observable<Generic[]>;
    prioritiesActive$!: Observable<Generic[]>;

    constructor(
        private dialogRef: MatDialogRef<AddCerereTypeComponent>,
        private requestTypesService: CerereTypeService,
        private notificationService: NotificationService,
        private commonService: CommonService,
        private authService: AuthService,
        private cererePriorityService: CererePriorityService,
        private statusService: StatusService
    ) {
        this.processToken();
    }

    ngOnInit() {
        this.loadDirectiiActive();
        this.loadStatususesActive();
        this.loadPrioritiesActive();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    processToken() {
        const tokenObj = this.authService.returnTokenUser();
        tokenObj.then((token) => {
            this.token = token;
            this.requestTypesService.setToken(token);
        });
    }

    submitForm() {
        this.isLoading = true;

        const {
            name,
            slug,
            priority_id,
            status_id,
            directie_id,
            compartiment_id,
            user_id,
            observations,
            indoc_name,
            indoc_id,
        } = this.form.value as CerereTypeAddPayload;
        const newCerereType: CerereTypeAddPayload = {
            name,
            slug,
            priority_id,
            status_id,
            directie_id,
            compartiment_id,
            user_id,
            observations,
            indoc_name,
            indoc_id,
        };

        this.requestTypesService
            .addRequestType(newCerereType)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    loadDirectiiActive() {
        this.isLoading = true;
        this.directiiActive$ = this.commonService.getDirectiiActive().pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => (this.isLoading = false))
        );
    }

    onDirectieChange(directieId: number): void {
        if (directieId) {
            this.loadCompartimenteActive(directieId);
            this.loadUsersActive();
        } else {
            this.compartimenteActive$ = new Observable<Generic[]>((observer) => {
                observer.next([{ name: '--Alegeți--', id: -1 }]);
                observer.complete();
            });

            console.log(this.compartimenteActive$, 'check here pls');
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectieId(directieId)
            : this.commonService.getCompartimenteActive();

        this.compartimenteActive$ = compartimenteObservable$.pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => {
                this.isLoading = false;
            })
        );
    }

    onDirectieCompartimentChange(): void {
        this.loadUsersActive();
    }

    loadStatususesActive() {
        this.isLoading = true;
        this.statusesActive$ = this.commonService.getCerereStatusesActive().pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => (this.isLoading = false))
        );
    }

    loadPrioritiesActive() {
        this.isLoading = true;
        this.prioritiesActive$ = this.commonService
            .getCererePrioritiesActive()
            .pipe(
                map((results: any) => [this.defaultParameter, ...results]),
                finalize(() => (this.isLoading = false))
            );
    }

    loadUsersActive() {
        this.isLoading = true;
        this.usersActive$ = this.commonService
            .getUsersByOrganigrama(this.form.value)
            .pipe(
                map((results: any) => [this.defaultParameter, ...results]),
                finalize(() => (this.isLoading = false))
            );
    }
}
